import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authPostRequest } from "../../api/authClient";
import { useAuthMe } from "../../hooks/useAuthMe";
import { NiadLogo } from "../../Components/NiadLogo";

type RegisterPayload = {
  full_name: string;
  company_name: string;
  company_location: string;
  email: string;
  password: string;
  roles: string[];
  country: string;
  site_map: string;
  company_logo: string;
  snapshot_domain: string;
  webrtc_domain: string;
  hls_domain: string;
  recording_domain: string;
  gpus: string;
  designation?: string;
  contact?: string;
  department?: string;
  level?: number;
  isEmailEnable?: boolean;
  isWhatsappEnable?: boolean;
};

function isValidEmail(val: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const me = useAuthMe({ redirectOn401: false });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    contact: "",
    country: "",
    company_name: "",
    designation: "",
    department: "",
    password: "",
  });

  useEffect(() => {
    if (me.data && !me.isError) {
      navigate("/", { replace: true });
    }
  }, [me.data, me.isError, navigate]);

  const setField = (name: keyof typeof form, value: string) => {
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.full_name.trim()) return toast.error("Please enter your name.");
    if (!form.email.trim() || !isValidEmail(form.email)) return toast.error("Please enter a valid email.");
    if (!form.contact.trim()) return toast.error("Please enter contact number.");
    if (!form.country.trim()) return toast.error("Please select your country.");
    if (!form.company_name.trim()) return toast.error("Please enter company name.");
    if (!form.designation.trim()) return toast.error("Please enter designation.");
    if (!form.department.trim()) return toast.error("Please enter department.");
    if (!form.password.trim()) return toast.error("Please enter a password.");

    setIsLoading(true);
    try {
      const payload: RegisterPayload = {
        full_name: form.full_name.trim(),
        company_name: form.company_name.trim(),
        // Keep a non-empty value to avoid backend rejecting missing field.
        company_location: "N/A",
        email: form.email.trim(),
        password: form.password,
        roles: ["VIEWER"],
        country: form.country.trim(),
        site_map: "",
        company_logo: "",
        snapshot_domain: "",
        webrtc_domain: "",
        hls_domain: "",
        recording_domain: "",
        gpus: "",
        designation: form.designation.trim(),
        contact: form.contact.trim(),
        department: form.department.trim(),
        isEmailEnable: true,
        isWhatsappEnable: false,
      };

      await authPostRequest("/client/auth/register", payload);
      toast.success("Registration successful. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === "string" && msg.length > 0 ? msg : "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200/60 bg-white/70 backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/40">
        <div className="flex items-center gap-3">
          <NiadLogo alt="NIAD Group." className="p-1" />
        </div>
        <div className="flex items-center gap-2">
          {/* reserved for future header actions (theme/apps) */}
        </div>
      </header>

      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-2xl">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-emerald-900/5 dark:shadow-slate-900/50 border border-slate-200/60 dark:border-slate-700/60 p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Create Account</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Register to get started with Bizeye</p>
            </div>

            <form className="space-y-5" onSubmit={onSubmit}>
              <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/30 p-6">
             

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="full_name" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="full_name"
                      type="text"
                      value={form.full_name}
                      onChange={(e) => setField("full_name", e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      required
                      autoComplete="email"
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Contact <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact"
                      type="tel"
                      value={form.contact}
                      onChange={(e) => setField("contact", e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter contact number (max 10 dig)"
                    />
                  </div>

                  <div>
                    <label htmlFor="country" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="country"
                      value={form.country}
                      onChange={(e) => setField("country", e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none"
                    >
                      <option value="">Select your country</option>
                      <option value="IN">India</option>
                      <option value="US">United States</option>
                      <option value="AE">United Arab Emirates</option>
                      <option value="SG">Singapore</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="company_name" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="company_name"
                      type="text"
                      value={form.company_name}
                      onChange={(e) => setField("company_name", e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter your company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="designation" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="designation"
                      type="text"
                      value={form.designation}
                      onChange={(e) => setField("designation", e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter your designation"
                    />
                  </div>

                  <div>
                    <label htmlFor="department" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="department"
                      type="text"
                      value={form.department}
                      onChange={(e) => setField("department", e.target.value)}
                      required
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter your department"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-slate-300">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="text-xs text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 transition-colors"
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      required
                      autoComplete="new-password"
                      className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                      placeholder="Enter password"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full p-3 rounded-lg text-white font-medium transition-all duration-200 bg-emerald-600 hover:bg-emerald-700 dark:bg-blue-600 dark:hover:bg-blue-500 focus:ring-2 focus:ring-emerald-500 dark:focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-800 disabled:opacity-70"
              >
                {isLoading ? "Please wait..." : "Next"}
              </button>
            </form>

          <p className="text-center text-sm text-gray-600 dark:text-slate-400 mt-4">
            Already have an account?{" "}
            <Link to="/login" className="text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
    </div>
  );
}

